import { motion } from 'motion/react'
import type { ReactNode } from 'react'

interface FeatureCardProps {
  icon: ReactNode
  title: string
  description: ReactNode
  /** Delay en segundos para stagger animation (multiplica por idx). */
  delayMultiplier?: number
  /** Si true, no anima al montar (útil cuando el grid cambia por tabs). */
  disableInitialAnimation?: boolean
}

/**
 * Card visual de feature (ventaja, ejemplo, diferenciador).
 *
 * Misma estructura visual que `.marketing-blog-card`:
 *  - Top accent bar morada (gradiente)
 *  - Background `var(--bg-card)` con borde sutil
 *  - Border-radius 14px, padding generoso
 *  - Hover: lift -4px + border accent + shadow morada + barra accent se intensifica
 *
 * Reusable en cualquier página que necesite listar ventajas, ejemplos o
 * diferenciadores. Acepta children en `description` para permitir JSX
 * con `<strong>` y `<br/>` en el contenido.
 *
 * Ejemplo:
 *   <FeatureGrid items={[
 *     { icon: <Shield />, title: 'Aislamiento', description: 'Tu servidor...' },
 *     { icon: <HardDrive />, title: 'Almacenamiento', description: '40-320 GB...' },
 *   ]} />
 */
export function FeatureCard({
  icon,
  title,
  description,
  delayMultiplier = 0.05,
  disableInitialAnimation = false,
}: FeatureCardProps) {
  return (
    <motion.div
      className="marketing-feature-item"
      initial={disableInitialAnimation ? false : { opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-50px' }}
      transition={{ duration: 0.5, delay: delayMultiplier }}
    >
      <span style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: '0.5rem' }}>
        <div className="marketing-feature-item-icon">{icon}</div>
        <h3>{title}</h3>
      </span>
      <div>{description}</div>
    </motion.div>
  )
}

interface FeatureItem {
  icon: ReactNode
  title: string
  description: ReactNode
}

interface FeatureGridProps {
  items: FeatureItem[]
}

/**
 * Grid de FeatureCards con layout responsive (1 col móvil → 2 cols desktop).
 * Reutiliza `.marketing-feature-grid` CSS.
 */
export function FeatureGrid({ items }: FeatureGridProps) {
  return (
    <div className="marketing-feature-grid">
      {items.map((item, idx) => (
        <FeatureCard
          key={item.title}
          icon={item.icon}
          title={item.title}
          description={item.description}
          delayMultiplier={idx * 0.05}
        />
      ))}
    </div>
  )
}